import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CIRCOLARI_IN_ARRIVO } from '../utils/circolariInArrivo';
import {
  BadgeCheck,
  User,
  KeyRound,
  Eye,
  EyeOff,
  Building2,
  Mail,
  Phone,
  CreditCard,
  CheckCircle,
  XCircle,
  LogOut,
  FileText,
  Award,
  Clock,
  Home,
  Hash,
  Briefcase,
  Receipt,
  FileCheck,
  MessageCircle,
  Building,
  MapPin,
  PlayCircle,
  X,
  ClipboardList,
  TrendingUp,
  Pencil,
  Save,
  Plus,
  Trash2,
  Camera,
  CheckSquare,
  Check,
  QrCode,
  Lock,
  Handshake,
  Star,
  ExternalLink,
  Shield,
  LifeBuoy
} from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import CardChatDashboard from '../components/chat/CardChatDashboard';
import {
  getAssociateData,
  logout,
  isAuthenticated,
  AssociateData,
  getCertifiedNetworkStatus,
  CertifiedNetworkStatus,
  refreshAssociateData,
  getAuthHeader,
  getAssociateProfile,
  updateAssociateProfile,
  uploadAssociateFoto,
  AssociateProfileData,
  setAssociatePassword,
  removeAssociatePassword
} from '../services/otpAuthService';
import ConfirmDialog from '../components/ConfirmDialog';
import { extractYouTubeId } from '../utils/youtube';
import VideoModal from '../components/VideoModal';
import AssociateLocationMap from '../components/AssociateLocationMap';
import CertifiedNetworkPayment from '../components/CertifiedNetworkPayment';
import STACodeCard from '../components/STACodeCard';
import AssociateFeeCard from '../components/AssociateFeeCard';
import PraticheAutoOnlineAccess from '../components/PraticheAutoOnlineAccess';
import IlMioAccessoDipendente from '../components/IlMioAccessoDipendente';
import GestioneDipendenti from '../components/dipendenti/GestioneDipendenti';
import AvvisoStudioNonInRegola, { useStudioNonInRegola } from '../components/dipendenti/AvvisoStudioNonInRegola';
import { codiceDaRisposta, motivoFineDipendente } from '../utils/sessioneAssociato';
import { CODICI_SEZIONE, eDipendente, nomeDipendente, sezioneVisibile } from '../utils/sezioniDipendente';
import { getPaymentStatus, PaymentStatusData, valutaGateQuota } from '../services/associatePaymentService';
import SedePagamentoBadge from '../components/admin/SedePagamentoBadge';
import axios from 'axios';

// API base REST (senza /api: lo aggiungono le singole chiamate)
const API_BASE_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace('/graphql', '')
  : 'http://localhost:5000';

const REST_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

const CATEGORIE_BADGE: Record<string, string> = {
  STUDIO: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  AUTOSCUOLA: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  NAUTICA: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  TUTTI: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

/**
 * Dashboard area riservata associati
 */
// Tipo per le sedi caricate dall'API
interface SedeInfo {
  id: string;
  sedeId: number | null;
  isPrincipal: boolean;
  nome: string;
  indirizzo?: string;
  civico?: string;
  cap?: string;
  citta?: string;
  provincia?: string;
  regione?: string;
  telefono?: string;
  email?: string;
  indirizzoCompleto: string;
  quoteIntegrative?: { anno: number }[];
  coordinates: {
    lat?: number;
    lng?: number;
    geocoded?: boolean;
    geocodeQuality?: string;
  } | null;
}

const AssociateDashboardPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [associate, setAssociate] = useState<AssociateData | null>(null);
  const [certifiedNetworkStatus, setCertifiedNetworkStatus] = useState<CertifiedNetworkStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  // Stato pagamento quota (caricato a livello pagina per il sollecito sopra le
  // schede, dato che la card Tesseramento vive nella scheda Profilo non default)
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatusData | null>(null);
  const [feedbackUnreadCount, setFeedbackUnreadCount] = useState(0);
  const [pendingReviews, setPendingReviews] = useState<Array<{_id: string; serviceType: string}>>([]);
  const [showVideoModal, setShowVideoModal] = useState(false);
  // Popup video benvenuto Rete Certificata (config da admin, dismiss per-associato)
  const [showRCWelcomeVideo, setShowRCWelcomeVideo] = useState(false);
  const [rcVideoDontShowAgain, setRcVideoDontShowAgain] = useState(false);
  const [showFacileModal, setShowFacileModal] = useState(false);
  // Scheda attiva area riservata: 'servizi' (default, in alto) | 'profilo'
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'servizi' | 'profilo'>(
    searchParams.get('tab') === 'profilo' ? 'profilo' : 'servizi'
  );

  // Password di accesso (alternativa all'OTP): form nella card del profilo.
  // ?password=cambia arriva dal login con password temporanea (AssociateLoginPage).
  const [pwdOpen, setPwdOpen] = useState(searchParams.get('password') === 'cambia');
  const [pwdCurrent, setPwdCurrent] = useState('');
  const [pwdNew, setPwdNew] = useState('');
  const [pwdConfirm, setPwdConfirm] = useState('');
  const [pwdShow, setPwdShow] = useState(false);
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdRemoveOpen, setPwdRemoveOpen] = useState(false);
  // Roving tabindex + navigazione tastiera (pattern ARIA tabs)
  const serviziTabRef = useRef<HTMLButtonElement>(null);
  const profiloTabRef = useRef<HTMLButtonElement>(null);
  const handleTabKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(e.key)) return;
    e.preventDefault();
    const target: 'servizi' | 'profilo' =
      e.key === 'Home' ? 'servizi'
      : e.key === 'End' ? 'profilo'
      : activeTab === 'servizi' ? 'profilo' : 'servizi';
    setActiveTab(target);
    (target === 'servizi' ? serviziTabRef : profiloTabRef).current?.focus();
  };

  // State per le sedi
  const [sedi, setSedi] = useState<SedeInfo[]>([]);
  const [selectedSedeIndex, setSelectedSedeIndex] = useState(0);
  const [loadingSedi, setLoadingSedi] = useState(true);
  const [sediError, setSediError] = useState<string | null>(null);

  // State profilo arricchito
  const [profile, setProfile] = useState<AssociateProfileData | null>(null);
  const [, setLoadingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profilePec, setProfilePec] = useState('');
  const [profileEmailSecondarie, setProfileEmailSecondarie] = useState<string[]>([]);
  const [profileTelefoniExtra, setProfileTelefoniExtra] = useState<string[]>([]);
  const [profileAttivita, setProfileAttivita] = useState<string[]>([]);
  const [allActivities, setAllActivities] = useState<Array<{ _id: string; nome: string; categoria: string }>>([]);
  const [editingProfile, setEditingProfile] = useState(false);
  const [uploadingFoto, setUploadingFoto] = useState(false);
  // Input "aggiungi" inline per contatti
  const [newEmailInput, setNewEmailInput] = useState<string | null>(null);
  const [newTelInput, setNewTelInput] = useState<string | null>(null);
  const [editingPec, setEditingPec] = useState(false);

  // State per modifica contatti sede secondaria
  const [editingSedeContact, setEditingSedeContact] = useState<string | null>(null);
  const [sedeContactForm, setSedeContactForm] = useState({ telefono: '', email: '' });
  const [savingSedeContact, setSavingSedeContact] = useState(false);

  // Dipendente: studio non in regola (403 STUDIO_NON_IN_REGOLA, Ruling R16) ⇒ sezioni chiuse e avviso
  const [studioNonInRegola, setStudioNonInRegola] = useStudioNonInRegola();

  useEffect(() => {
    // Dati della dashboard del dipendente, ognuno tollerante agli errori (403 da sezioni tolte nel frattempo)
    const caricaDatiDipendente = async (dati: AssociateData) => {
      // Stato della Rete Certificata: decide le card di voucher, svincolo, collaborazioni e
      // Facile.it con le stesse regole del titolare
      if ((['voucher', 'svincolo', 'collabora', 'facile'] as const).some((c) => sezioneVisibile(dati, c))) {
        try {
          setCertifiedNetworkStatus(await getCertifiedNetworkStatus());
        } catch { /* silenzioso: le card della Rete Certificata restano bloccate */ }
      }
      if (sezioneVisibile(dati, 'supporto')) {
        try {
          const { getMyTickets } = await import('../services/supportService');
          const ticketsRes = await getMyTickets({ page: 1, limit: 50 });
          setFeedbackUnreadCount((ticketsRes.items || []).filter((t) => t.unread?.associate).length);
        } catch { /* silenzioso: il badge non e critico */ }
      }
      if (sezioneVisibile(dati, 'recensioni')) {
        try {
          const reviewsRes = await axios.get(`${REST_BASE}/service-reviews/pending`, {
            headers: getAuthHeader() as any
          });
          if (reviewsRes.data.success) {
            setPendingReviews(reviewsRes.data.reviews || []);
          }
        } catch { /* silenzioso */ }
      }
      setLoadingSedi(false);
    };

    const loadData = async () => {
      // Verifica autenticazione
      if (!isAuthenticated()) {
        toast.error('Devi effettuare l\'accesso');
        navigate('/accesso-associati');
        return;
      }

      // Carica dati associato aggiornati dal server
      try {
        const freshData = await refreshAssociateData();
        setAssociate(freshData);
        setStudioNonInRegola(false);

        // Dipendente dello studio: si carica solo ciò che serve alle sue sezioni. Pagamenti,
        // profilo dello studio, sedi, video RC e area associativa restano del titolare (spec §6.3).
        if (eDipendente(freshData)) {
          await caricaDatiDipendente(freshData);
          return;
        }

        // Carica stato rete certificata
        const status = await getCertifiedNetworkStatus();
        setCertifiedNetworkStatus(status);

        // Apre il popup video benvenuto RC se il backend lo ha incluso
        if (status.welcomeVideo && extractYouTubeId(status.welcomeVideo.url)) {
          setShowRCWelcomeVideo(true);
        }

        // Carica stato pagamento quota (per il sollecito sopra le schede).
        // Tollerante al 404 (nessun settings pagamenti configurato).
        try {
          const payRes = await getPaymentStatus();
          setPaymentStatus(payRes.data);
        } catch { /* silenzioso: nessun sollecito quota se non disponibile */ }

        const { getAuthHeader } = await import('../services/otpAuthService');

        // Badge risposte non lette: ora dai ticket di supporto (il modulo feedback
        // e confluito nel canale "Feedback e segnalazioni" del Support Hub).
        try {
          const { getMyTickets } = await import('../services/supportService');
          const ticketsRes = await getMyTickets({ page: 1, limit: 50 });
          setFeedbackUnreadCount((ticketsRes.items || []).filter((t) => t.unread?.associate).length);
        } catch { /* silenzioso: il badge non e critico */ }

        // Carica recensioni pending
        try {
          const reviewsRes = await axios.get(`${REST_BASE}/service-reviews/pending`, {
            headers: getAuthHeader() as any
          });
          if (reviewsRes.data.success) {
            setPendingReviews(reviewsRes.data.reviews || []);
          }
        } catch { /* silenzioso */ }

        // Check if associate has a representative profile
        try {
          const { checkRepresentative } = await import('../services/representativeService');
          const repCheck = await checkRepresentative(freshData.id || (freshData as any)._id);
          if (repCheck.hasRepresentative) {
            setRepInfo(repCheck);
          }
        } catch { /* silenzioso */ }

        // Carica sedi per la mappa
        try {
          const sediRes = await axios.get(`${API_BASE_URL}/api/auth/my-sedi`, {
            headers: getAuthHeader() as any
          });
          if (sediRes.data.success) {
            setSedi(sediRes.data.data.sedi);
          }
        } catch (sediErr: any) {
          console.error('Errore caricamento sedi:', sediErr);
          setSediError(sediErr.response?.data?.message || 'Errore caricamento sedi');
        } finally {
          setLoadingSedi(false);
        }
      } catch (error) {
        console.error('Errore caricamento dati:', error);

        // Dipendente (Ruling R15/R16): sessione finita ⇒ logout, toast e accesso li fa FineSessioneDipendente;
        // studio non in regola ⇒ la sessione resta, dati salvati e sezioni chiuse. Il titolare non riceve questi codici.
        const risposta = (error as { response?: { status?: number; data?: unknown } } | null)?.response;
        const motivo = motivoFineDipendente(risposta?.status, codiceDaRisposta(risposta?.data));
        if (motivo === 'sessione') return;
        if (motivo === 'studio_non_in_regola' && eDipendente(getAssociateData())) setStudioNonInRegola(true);

        // Fallback: usa dati dal localStorage
        const cachedData = getAssociateData();
        if (cachedData) {
          setAssociate(cachedData);
        } else {
          toast.error('Dati associato non trovati');
          navigate('/accesso-associati');
        }
      } finally {
        setLoadingStatus(false);
      }
    };

    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, [navigate]);

  // Carica profilo arricchito e catalogo attività
  useEffect(() => {
    const loadProfile = async () => {
      if (!isAuthenticated()) return;
      // Il profilo è dello studio: al dipendente non serve (e il server non glielo dà)
      if (eDipendente(getAssociateData())) return;
      setLoadingProfile(true);
      try {
        const [prof, activitiesRes] = await Promise.all([
          getAssociateProfile(),
          axios.get(`${REST_BASE}/activities`)
        ]);
        setProfile(prof);
        setProfilePec(prof.pec || '');
        setProfileEmailSecondarie(prof.emailSecondarie || []);
        setProfileTelefoniExtra(prof.telefoniExtra || []);
        setProfileAttivita((prof.attivitaSvolte || []).map((a: any) => a._id || a));
        setAllActivities(activitiesRes.data.activities || []);
      } catch (err) {
        console.error('Errore caricamento profilo:', err);
      } finally {
        setLoadingProfile(false);
      }
    };
    loadProfile();
  }, []);

  const resetPwdForm = () => { setPwdCurrent(''); setPwdNew(''); setPwdConfirm(''); setPwdOpen(false); };

  const handleSavePassword = async () => {
    const authInfo = associate?.auth;
    if (pwdNew.length < 8) { toast.error('La password deve avere almeno 8 caratteri'); return; }
    if (pwdNew !== pwdConfirm) { toast.error('Le due password non coincidono'); return; }
    setPwdSaving(true);
    try {
      const body: { currentPassword?: string; newPassword: string } = { newPassword: pwdNew };
      if (authInfo?.hasPassword && !authInfo.temporary) body.currentPassword = pwdCurrent;
      const res = await setAssociatePassword(body);
      if (res.success) {
        toast.success(authInfo?.hasPassword ? 'Password aggiornata' : 'Password impostata: da ora puoi accedere anche senza codice');
        resetPwdForm();
        setAssociate(await refreshAssociateData());
      } else {
        toast.error(res.message || 'Impossibile salvare la password');
      }
    } catch (e: any) {
      toast.error(e.message || 'Errore durante il salvataggio della password');
    } finally {
      setPwdSaving(false);
    }
  };

  const handleRemovePassword = async () => {
    setPwdRemoveOpen(false);
    try {
      const res = await removeAssociatePassword(pwdCurrent);
      if (res.success) {
        toast.success('Password rimossa: accederai solo con il codice via email');
        resetPwdForm();
        setAssociate(await refreshAssociateData());
      } else {
        toast.error(res.message || 'Impossibile rimuovere la password');
      }
    } catch (e: any) {
      toast.error(e.message || 'Errore durante la rimozione della password');
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const updated = await updateAssociateProfile({
        pec: profilePec,
        emailSecondarie: profileEmailSecondarie,
        telefoniExtra: profileTelefoniExtra,
        attivitaSvolte: profileAttivita,
      });
      setProfile(updated);
      setEditingProfile(false);
      toast.success('Profilo aggiornato');
    } catch {
      toast.error('Errore nel salvataggio del profilo');
    } finally {
      setSavingProfile(false);
    }
  };

  // Salvataggio immediato dei soli campi contatto (PEC / email extra / tel extra)
  const saveContactsNow = async (overrides: { pec?: string; emailSecondarie?: string[]; telefoniExtra?: string[] }) => {
    setSavingProfile(true);
    try {
      const updated = await updateAssociateProfile({
        pec: overrides.pec !== undefined ? overrides.pec : profilePec,
        emailSecondarie: overrides.emailSecondarie !== undefined ? overrides.emailSecondarie : profileEmailSecondarie,
        telefoniExtra: overrides.telefoniExtra !== undefined ? overrides.telefoniExtra : profileTelefoniExtra,
        attivitaSvolte: profileAttivita,
      });
      setProfile(updated);
    } catch {
      toast.error('Errore nel salvataggio');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleConfirmEmail = async () => {
    if (!newEmailInput?.trim()) { setNewEmailInput(null); return; }
    const newList = [...profileEmailSecondarie, newEmailInput.trim()];
    setProfileEmailSecondarie(newList);
    setNewEmailInput(null);
    await saveContactsNow({ emailSecondarie: newList });
  };

  const handleRemoveEmail = async (i: number) => {
    const newList = profileEmailSecondarie.filter((_, j) => j !== i);
    setProfileEmailSecondarie(newList);
    await saveContactsNow({ emailSecondarie: newList });
  };

  const handleConfirmTel = async () => {
    if (!newTelInput?.trim()) { setNewTelInput(null); return; }
    const newList = [...profileTelefoniExtra, newTelInput.trim()];
    setProfileTelefoniExtra(newList);
    setNewTelInput(null);
    await saveContactsNow({ telefoniExtra: newList });
  };

  const handleRemoveTel = async (i: number) => {
    const newList = profileTelefoniExtra.filter((_, j) => j !== i);
    setProfileTelefoniExtra(newList);
    await saveContactsNow({ telefoniExtra: newList });
  };

  const handleConfirmPec = async () => {
    setEditingPec(false);
    await saveContactsNow({ pec: profilePec });
  };

  const handleRemovePec = async () => {
    setProfilePec('');
    setEditingPec(false);
    await saveContactsNow({ pec: '' });
  };

  const handleFotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFoto(true);
    try {
      const url = await uploadAssociateFoto(file);
      setProfile(prev => prev ? { ...prev, foto: url } : { foto: url });
      toast.success('Foto caricata con successo');
    } catch {
      toast.error('Errore nel caricamento della foto');
    } finally {
      setUploadingFoto(false);
    }
  };

  const toggleAttivita = (id: string) => {
    setProfileAttivita(prev =>
      prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
    );
  };

  const handleLogout = () => {
    logout();
    toast.success('Logout effettuato');
    navigate('/');
  };

  /**
   * Ricarica lo stato della rete certificata dopo un pagamento
   */
  const handleDownloadCertificate = async () => {
    try {
      const response = await axios.get(`${REST_BASE}/auth/my-certificate-pdf`, {
        responseType: 'blob',
        headers: getAuthHeader() as any
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Certificato_RC_UNASCA.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Errore download certificato');
    }
  };

  const handlePaymentSuccess = async () => {
    try {
      const status = await getCertifiedNetworkStatus();
      setCertifiedNetworkStatus(status);
      toast.success('Stato aggiornato con successo');
    } catch (error) {
      console.error('Errore ricaricamento stato:', error);
      toast.error('Errore durante l\'aggiornamento dello stato');
    }
  };

  /**
   * Ricarica le sedi
   */
  const reloadSedi = async () => {
    try {
      const { getAuthHeader } = await import('../services/otpAuthService');
      const sediRes = await axios.get(`${API_BASE_URL}/api/auth/my-sedi`, {
        headers: getAuthHeader() as any
      });
      if (sediRes.data.success) {
        setSedi(sediRes.data.data.sedi);
      }
    } catch (err) {
      console.error('Errore ricaricamento sedi:', err);
    }
  };

  /**
   * Salva i contatti di una sede secondaria
   */
  const handleSaveSedeContact = async () => {
    if (!editingSedeContact) return;

    try {
      setSavingSedeContact(true);
      const { getAuthHeader } = await import('../services/otpAuthService');
      const response = await axios.put(
        `${API_BASE_URL}/api/auth/update-sede-contact`,
        {
          sedeId: editingSedeContact,
          telefono: sedeContactForm.telefono,
          email: sedeContactForm.email
        },
        { headers: getAuthHeader() as any }
      );

      if (response.data.success) {
        toast.success('Contatti aggiornati con successo');
        setEditingSedeContact(null);
        setSedeContactForm({ telefono: '', email: '' });
        await reloadSedi();
      } else {
        toast.error(response.data.message || 'Errore nel salvataggio');
      }
    } catch (error: any) {
      console.error('Errore salvataggio contatti sede:', error);
      toast.error(error.response?.data?.message || 'Errore nel salvataggio dei contatti');
    } finally {
      setSavingSedeContact(false);
    }
  };

  /**
   * Apre il form di modifica contatti per una sede
   */
  const handleEditSedeContact = (sede: SedeInfo) => {
    setEditingSedeContact(sede.id);
    setSedeContactForm({
      telefono: sede.telefono || '',
      email: sede.email || ''
    });
  };

  /**
   * Gestisce il click sui link riservati ai membri attivi
   * Se il membro non è attivo, mostra un popup di avviso
   */
  const handleProtectedLinkClick = (path: string, e: React.MouseEvent) => {
    e.preventDefault();

    // Verifica se è membro attivo della rete certificata
    if (!certifiedNetworkStatus?.isMember) {
      toast.error(
        'Accesso riservato ai membri della Rete Certificata UNASCA',
        {
          duration: 5000,
          icon: '🔒',
        }
      );
      return;
    }

    if (!certifiedNetworkStatus?.isActive) {
      toast.error(
        'Il tuo account non è ancora stato attivato. Attendi l\'attivazione da parte della Segreteria Nazionale UNASCA.',
        {
          duration: 6000,
          icon: '⏳',
        }
      );
      return;
    }

    // Se è attivo, naviga alla pagina
    navigate(path);
  };

  /**
   * Gestisce il click sulle sezioni "In arrivo"
   * Mostra un messaggio che la sezione non è ancora attiva
   */
  const handleComingSoonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    toast.error(
      'Sezione non ancora attiva. Si prega di attendere.',
      {
        duration: 5000,
        icon: '⏳',
      }
    );
  };

  // Helper: l'utente è membro attivo della rete certificata?
  const isCertifiedActive = certifiedNetworkStatus?.isMember && certifiedNetworkStatus?.isActive;
  // Verifica Takata: aperta a TUTTI gli associati in regola con la quota (non solo Rete Certificata)
  const isInRegolaQuota = associate?.stato === 'ASSOCIATO'
    && (associate?.statoTesseramento === 'PAGATO' || associate?.statoTesseramento === 'ATTIVATO');
  // Circolari: stesso confronto del server (utils/tesseramentoStati.js isInRegola), senza
  // distinzione di maiuscole, così la card non si blocca per dati storici in minuscolo.
  const statoCircolari = String(associate?.stato || '').toUpperCase();
  const tesseramentoCircolari = String(associate?.statoTesseramento || '').toUpperCase();
  const isInRegolaCircolari = statoCircolari === 'ASSOCIATO'
    && (tesseramentoCircolari === 'PAGATO' || tesseramentoCircolari === 'ATTIVATO');
  // Verifica vera e propria: porta al registro, che è la pagina d'ingresso dello strumento
  // (istruzioni per l'estensione + elenco delle verifiche già fatte).
  const handleVerificaTakataClick = (e: React.MouseEvent) => {
    if (isInRegolaQuota) {
      navigate('/area-riservata/takata');
    } else {
      e.preventDefault();
      toast.error('Servizio riservato agli associati in regola con la quota associativa.');
    }
  };

  const closeRCWelcomeVideo = () => {
    setShowRCWelcomeVideo(false);
    const video = certifiedNetworkStatus?.welcomeVideo;
    if (rcVideoDontShowAgain && video) {
      // Fire-and-forget: se fallisce, il popup riapparirà al prossimo accesso
      updateAssociateProfile({ rcWelcomeVideoDismissedUrl: video.url }).catch(() => {});
    }
  };

  // Representative check state
  const [repInfo, setRepInfo] = useState<{ hasRepresentative: boolean; representativeId?: string; status?: string; hasPin?: boolean; caricaPrincipale?: string; caricheAttive?: number; canAccessArea?: boolean } | null>(null);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');

  if (!associate) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-50 dark:from-gray-950 dark:via-blue-950/20 dark:to-gray-950">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 relative">
            <div className="absolute inset-0 rounded-full border-2 border-blue-200 dark:border-blue-900"></div>
            <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-blue-600 animate-spin"></div>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 tracking-wide">Caricamento in corso…</p>
        </div>
      </div>
    );
  }

  const greeting = (() => {
    const h = new Date().getHours();
    if (h >= 5 && h < 12) return 'Buongiorno';
    if (h >= 12 && h < 18) return 'Buon pomeriggio';
    if (h >= 18 && h < 22) return 'Buonasera';
    return 'Buonanotte';
  })();

  const handleCopyCodice = () => {
    navigator.clipboard.writeText(associate.codice).then(() => {
      toast.success('Codice copiato!', { duration: 1500, icon: '📋' });
    });
  };

  // I pagamenti (quota associativa e certificato Rete Certificata) vivono nella
  // scheda "Il mio profilo" (non default): mostriamo un sollecito sopra le schede
  // SOLO quando esiste davvero un'azione di pagamento, replicando le condizioni di
  // AssociateFeeCard e CertifiedNetworkPayment per non generare falsi allarmi.
  // Gate della quota nazionale deciso dal server (puoPagareQuota); con backend che
  // non lo espone vale il gate storico IN ISCRIZIONE + IN ATTIVAZIONE e nessun
  // pagamento principale in corso. Se lo stato pagamenti non e' stato caricato
  // (paymentStatus null) non si mostra alcun sollecito quota.
  const gateQuota = valutaGateQuota(paymentStatus, associate);
  const canPayNational = gateQuota.puoPagare;
  // Le integrative con bonifico gia' in verifica non richiedono azioni dall'associato
  const hasPendingIntegrativa =
    associate.statoTesseramento === 'PAGATO' &&
    (paymentStatus?.pendingIntegrative ?? []).some(
      (pi) => !pi.bonificoCaricato && pi.metodoPagamento !== 'bonifico'
    );
  const certNeedsPayment =
    certifiedNetworkStatus?.currentCertificateStatus === 'IN ATTESA';
  // Dipendente dello studio: vede solo le card delle sezioni abilitate dal titolare; pagamenti,
  // profilo dello studio, area associativa e Rete Certificata (iscrizione, pagamento) restano del titolare.
  const dipendente = eDipendente(associate);
  // Nessuna card da mostrare al dipendente (guida formalità e recensioni non hanno una card)
  const dipendenteSenzaCard = dipendente
    && !CODICI_SEZIONE.some((c) => c !== 'formalita' && c !== 'recensioni' && sezioneVisibile(associate, c));
  const profiloNeedsAttention =
    !dipendente && (canPayNational || hasPendingIntegrativa || certNeedsPayment);
  // Rinnovo aperto per chi e' in regola: non e' un pagamento "in sospeso", il tono e' informativo
  const soloRinnovo =
    canPayNational && gateQuota.tipo === 'rinnovo' && !hasPendingIntegrativa && !certNeedsPayment;

  // Account dimostrativo per il partner Facile.it: niente scheda Profilo, niente
  // solleciti, nella griglia dei servizi resta solo la card Facile.
  const soloFacile = !!associate.demoFacile;

  const cardFacile = (
    <button
      onClick={(e) => isCertifiedActive ? setShowFacileModal(true) : handleProtectedLinkClick('', e)}
      className={`group relative rounded-xl p-5 transition-all duration-300 text-left overflow-hidden border-2 ${
        isCertifiedActive
          ? 'bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 border-orange-200 dark:border-orange-700 hover:shadow-xl hover:scale-105 cursor-pointer'
          : 'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800/40 dark:to-gray-700/40 border-gray-300 dark:border-gray-600 opacity-50 grayscale cursor-not-allowed'
      }`}
    >
      <div className="absolute top-3 right-3 z-10">
        {isCertifiedActive ? (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-orange-500/15 text-orange-700 dark:text-orange-400">
            Rete Certificata
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-500/80 text-white shadow-md">
            <Lock className="w-3 h-3" /> Rete Certificata
          </span>
        )}
      </div>
      <div className="absolute top-0 right-0 w-20 h-20 bg-orange-500/10 rounded-full -mr-10 -mt-10"></div>
      <div className="relative">
        <img
          src="/images/logo-facile-it.jpg"
          alt="Logo Facile.it"
          className={`w-12 h-12 rounded-xl object-cover mb-3 shadow-lg ${isCertifiedActive ? 'group-hover:scale-110 transition-transform' : ''}`}
        />
        <p className={`text-sm font-bold mb-1 ${isCertifiedActive ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>Partnership Facile.it</p>
        <p className={`text-xs ${isCertifiedActive ? 'text-gray-600 dark:text-gray-400' : 'text-gray-400 dark:text-gray-500'}`}>Passaggi di proprietà, verifica veicoli e RC Auto per i tuoi clienti</p>
      </div>
    </button>
  );

  return (
    <>
    <style>{`
      @keyframes fadeSlideUp {
        from { opacity: 0; transform: translateY(20px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      @keyframes shimmerSweep {
        0%   { transform: translateX(-100%) skewX(-20deg); }
        100% { transform: translateX(350%) skewX(-20deg); }
      }
      .anim-fade-up { animation: fadeSlideUp 0.55s cubic-bezier(.22,.68,0,1.2) both; }
      @keyframes ctaGlow {
        0%, 100% { box-shadow: 0 0 8px rgba(59,130,246,0.3), 0 0 24px rgba(59,130,246,0.1); }
        50% { box-shadow: 0 0 16px rgba(59,130,246,0.5), 0 0 40px rgba(59,130,246,0.2); }
      }
      .cta-certified {
        animation: ctaGlow 2.5s ease-in-out infinite;
      }
      .cta-certified::after {
        content: '';
        position: absolute; top: 0; left: 0; bottom: 0; width: 40%;
        background: linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent);
        animation: shimmerSweep 4s ease-in-out infinite;
        pointer-events: none;
        border-radius: inherit;
      }
      .hero-shimmer::after {
        content: '';
        position: absolute; top: 0; left: 0; bottom: 0; width: 40%;
        background: linear-gradient(90deg, transparent, rgba(255,255,255,0.10), transparent);
        animation: shimmerSweep 5s ease-in-out infinite;
        pointer-events: none;
        border-radius: inherit;
      }
    `}</style>
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-50 dark:from-gray-950 dark:via-blue-950/20 dark:to-gray-950">
      {/* Header Elegante */}
      <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border-b border-gray-200/50 dark:border-gray-800/50 sticky top-16 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2 min-w-0">
            <div className="flex items-center gap-3 min-w-0">
              {/* Avatar header — foto profilo o iniziale */}
              <div className="relative flex-shrink-0" title={associate.nominativo}>
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl overflow-hidden bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/30 border-2 border-white dark:border-gray-800">
                  {profile?.foto ? (
                    <img
                      src={`${REST_BASE.replace('/api', '')}${profile.foto}`}
                      alt="Foto profilo"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-base font-bold text-white">
                      {associate.nominativo?.charAt(0)?.toUpperCase() || '?'}
                    </span>
                  )}
                </div>
                {/* Bottone carica foto sull'avatar (la foto è dello studio: solo il titolare) */}
                {!dipendente && (
                <label className="absolute -bottom-1 -right-1 w-5 h-5 bg-blue-600 hover:bg-blue-700 rounded-full flex items-center justify-center cursor-pointer shadow transition-colors" title="Cambia foto">
                  <Camera className="w-2.5 h-2.5 text-white" />
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleFotoUpload} disabled={uploadingFoto} />
                </label>
                )}
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-widest text-blue-500 dark:text-blue-400 mb-0.5">{greeting}</p>
                <p className="text-sm sm:text-base font-bold text-gray-900 dark:text-white leading-tight truncate">{associate.nominativo}</p>
                {dipendente ? (
                  <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{nomeDipendente(associate)}</p>
                ) : (
                  <p className="text-xs text-gray-400 dark:text-gray-500">Area riservata</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
              <button
                onClick={() => setShowVideoModal(true)}
                className="flex items-center gap-1.5 px-2 sm:px-4 py-2 text-sm font-medium text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-all"
                title="Guarda il video tutorial"
              >
                <PlayCircle className="w-4 h-4 flex-shrink-0" />
                <span className="hidden sm:inline">Video Tutorial</span>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-1.5 px-2 sm:px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all"
              >
                <LogOut className="w-4 h-4 flex-shrink-0" />
                <span className="hidden sm:inline">Esci</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Banner Recensioni Pending */}
        {pendingReviews.length > 0 && (
          <div className="mb-4 bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-yellow-100 dark:bg-yellow-900/40 rounded-full flex items-center justify-center">
                <Star className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {pendingReviews.length === 1
                    ? 'Hai una recensione da lasciare'
                    : `Hai ${pendingReviews.length} recensioni da lasciare`}
                </p>
                <p className="text-2xs text-gray-500 dark:text-gray-400">
                  La tua opinione aiuta i colleghi a scegliere i servizi migliori
                </p>
              </div>
              <a
                href={`/area-riservata/recensione/${pendingReviews[0]._id}`}
                className="flex-shrink-0 px-3 py-1.5 bg-yellow-500 hover:bg-yellow-600 text-white text-sm rounded-lg font-medium transition-colors"
              >
                Lascia recensione
              </a>
            </div>
          </div>
        )}

        {/* Hero Card con Info Principali */}
        <div className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 rounded-2xl shadow-xl p-5 sm:p-8 mb-6 sm:mb-8 text-white overflow-hidden hero-shimmer anim-fade-up" style={{ animationDelay: '0ms' }}>
          {/* Decorazione geometrica sfondo */}
          <div className="absolute top-0 right-0 w-64 h-64 opacity-[0.07]" aria-hidden="true">
            <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
              <circle cx="150" cy="50" r="80" fill="white"/>
              <circle cx="50" cy="150" r="60" fill="white"/>
            </svg>
          </div>
          <div className="absolute bottom-0 left-0 w-48 h-48 opacity-[0.05]" aria-hidden="true">
            <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <circle cx="20" cy="80" r="60" fill="white"/>
            </svg>
          </div>

          <div className="relative grid grid-cols-3 gap-3 sm:gap-8">
            {/* Codice Associato */}
            <div>
              <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-widest opacity-70 mb-1.5 sm:mb-2 flex items-center gap-1">
                <Building2 className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                <span className="hidden sm:inline">Codice Associato</span>
                <span className="sm:hidden">Codice</span>
              </p>
              <button
                onClick={handleCopyCodice}
                className="group inline-flex items-center gap-1 sm:gap-2 font-mono text-base sm:text-2xl font-bold bg-white/15 border border-white/25 rounded-lg px-2 sm:px-3 py-0.5 sm:py-1 tracking-wider hover:bg-white/25 transition-colors cursor-pointer"
                title="Clicca per copiare"
              >
                {associate.codice}
                <span className="opacity-0 group-hover:opacity-60 transition-opacity hidden sm:inline">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </span>
              </button>
            </div>

            {/* Categoria */}
            <div>
              <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-widest opacity-70 mb-1.5 sm:mb-2 flex items-center gap-1">
                <Briefcase className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" /> Categoria
              </p>
              <p className="text-lg sm:text-2xl font-bold">{associate.categoria || 'N/D'}</p>
              {associate.integrativa && (
                <p className="text-[10px] sm:text-xs opacity-60 mt-0.5">+ {associate.integrativa}</p>
              )}
            </div>

            {/* Stato */}
            <div>
              <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-widest opacity-70 mb-1.5 sm:mb-2 flex items-center gap-1">
                <CheckCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" /> Stato
              </p>
              {associate.stato === 'ASSOCIATO' ? (
                <span className="inline-flex items-center gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full bg-green-400/20 border border-green-400/40 text-green-200 text-xs sm:text-sm font-semibold">
                  <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-400 rounded-full animate-pulse flex-shrink-0"></span>
                  Attivo
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full bg-red-400/20 border border-red-400/40 text-red-200 text-xs sm:text-sm font-semibold">
                  <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-red-400 rounded-full flex-shrink-0"></span>
                  {associate.stato}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Sollecito pagamenti: la card paga-quota e il pagamento del certificato
            Rete Certificata vivono nella scheda Profilo (non default). Qui resta
            visibile all'apertura SOLO se esiste davvero un'azione di pagamento. Mostrato
            solo sulla scheda Servizi: in Profilo le card di pagamento sono gia visibili. */}
        {activeTab === 'servizi' && profiloNeedsAttention && !soloFacile && (
          <div className={`mb-4 bg-gradient-to-r border rounded-xl p-4 ${soloRinnovo
            ? 'from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800'
            : 'from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200 dark:border-amber-800'}`}>
            <div className="flex items-center gap-3">
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${soloRinnovo ? 'bg-blue-100 dark:bg-blue-900/40' : 'bg-amber-100 dark:bg-amber-900/40'}`}>
                <CreditCard className={`w-5 h-5 ${soloRinnovo ? 'text-blue-600 dark:text-blue-400' : 'text-amber-600 dark:text-amber-400'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {soloRinnovo ? `È aperto il rinnovo ${gateQuota.anno}` : 'Hai un pagamento in sospeso'}
                </p>
                <p className="text-2xs text-gray-500 dark:text-gray-400">
                  {soloRinnovo ? 'Sei in regola: apri "Il mio profilo" per rinnovare la quota' : 'Apri "Il mio profilo" per completarlo'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setActiveTab('profilo')}
                className={`flex-shrink-0 px-3 py-1.5 text-white text-sm rounded-lg font-medium transition-colors ${soloRinnovo ? 'bg-blue-600 hover:bg-blue-700' : 'bg-amber-500 hover:bg-amber-600'}`}
              >
                Vai al profilo
              </button>
            </div>
          </div>
        )}

        {/* Barra schede: Servizi e Azioni | Il mio profilo (nascosta per l'account demo Facile) */}
        {!soloFacile && (
        <>
        <div className="flex flex-wrap items-center gap-2 mb-2" role="tablist" aria-label="Sezioni area riservata" onKeyDown={handleTabKeyDown}>
          <button
            ref={serviziTabRef}
            type="button"
            role="tab"
            id="tab-servizi"
            aria-selected={activeTab === 'servizi'}
            tabIndex={activeTab === 'servizi' ? 0 : -1}
            onClick={() => setActiveTab('servizi')}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 border-2 ${
              activeTab === 'servizi'
                ? 'bg-gradient-to-br from-indigo-600 to-indigo-700 text-white border-indigo-500 shadow-lg shadow-indigo-500/25'
                : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-700'
            }`}
          >
            <Briefcase className="w-4 h-4" />
            Servizi e Azioni
          </button>
          <button
            ref={profiloTabRef}
            type="button"
            role="tab"
            id="tab-profilo"
            aria-selected={activeTab === 'profilo'}
            tabIndex={activeTab === 'profilo' ? 0 : -1}
            onClick={() => setActiveTab('profilo')}
            className={`relative inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 border-2 ${
              activeTab === 'profilo'
                ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white border-blue-500 shadow-lg shadow-blue-500/25'
                : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700'
            }`}
          >
            <User className="w-4 h-4" />
            {dipendente ? 'Il mio accesso' : 'Il mio profilo'}
            {profiloNeedsAttention && (
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-500 rounded-full ring-2 ring-white dark:ring-gray-950 animate-pulse" aria-hidden="true" />
            )}
          </button>
        </div>
        <p className="hidden sm:block text-xs text-gray-400 dark:text-gray-500 mb-6 ml-1">
          {activeTab === 'servizi'
            ? 'Tutto quello che puoi fare, subito in alto'
            : (dipendente ? 'Il tuo nome, lo studio e la password' : 'Dati anagrafici, tesseramento e sede')}
        </p>
        </>
        )}

        {/* —— SCHEDA: Il mio accesso (dipendente dello studio) —— */}
        {activeTab === 'profilo' && !soloFacile && dipendente && (
          <div className="mb-8" role="tabpanel" id="panel-profilo" aria-labelledby="tab-profilo">
            <IlMioAccessoDipendente associate={associate} />
          </div>
        )}

        {/* —— SCHEDA: Il mio profilo —— */}
        {activeTab === 'profilo' && !soloFacile && !dipendente && (
        /* Layout a 2 Colonne */
        <div className="grid lg:grid-cols-3 gap-6 mb-8" role="tabpanel" id="panel-profilo" aria-labelledby="tab-profilo">
          {/* Colonna Sinistra - Dati Anagrafici */}
          <div className="lg:col-span-2 space-y-6">
            {/* Card Dati Anagrafici */}
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-800 p-6 transition-shadow duration-200 hover:shadow-xl anim-fade-up" style={{ animationDelay: '80ms' }}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-md shadow-blue-500/20">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                  Dati Anagrafici
                </h2>
              </div>

              {/* Dati in grid */}
              <div className="space-y-4">
                {/* Ragione Sociale */}
                <div className="pb-4 border-b border-gray-100 dark:border-gray-800">
                  <label className="text-xs font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-1 block">
                    Ragione Sociale
                  </label>
                  <p className="text-base font-semibold text-gray-900 dark:text-white">
                    {associate.nominativo}
                  </p>
                </div>

                {/* Grid 2 colonne per dati fiscali */}
                <div className="grid md:grid-cols-2 gap-4 pb-4 border-b border-gray-100 dark:border-gray-800">
                  {/* Partita IVA */}
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1 flex items-center gap-1">
                      <Hash className="w-3 h-3" />
                      Partita IVA
                    </label>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      {associate.partitaIVA}
                    </p>
                  </div>

                  {/* Codice Fiscale */}
                  {associate.codiceFiscale && (
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1 flex items-center gap-1">
                        <Hash className="w-3 h-3" />
                        Codice Fiscale
                      </label>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {associate.codiceFiscale}
                      </p>
                    </div>
                  )}
                </div>

                {/* Rappresentanti */}
                {(associate.rapprLegale || associate.rappresentante) && (
                  <div className="grid md:grid-cols-2 gap-4 pb-4 border-b border-gray-100 dark:border-gray-800">
                    {(associate.rapprLegale || associate.rapprLegaleNome || associate.rapprLegaleCognome) && (
                      <div>
                        <label className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1 flex items-center gap-1">
                          <User className="w-3 h-3" />
                          Rappresentante Legale
                        </label>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {[associate.rapprLegaleCognome, associate.rapprLegaleNome].filter(Boolean).join(' ') || associate.rapprLegale}
                        </p>
                      </div>
                    )}
                    {(associate.rappresentante || associate.rappresentanteNome || associate.rappresentanteCognome) && (
                      <div>
                        <label className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1 flex items-center gap-1">
                          <User className="w-3 h-3" />
                          Rappresentante Associativo
                        </label>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {[associate.rappresentanteCognome, associate.rappresentanteNome].filter(Boolean).join(' ') || associate.rappresentante}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Grid 3 colonne: Email | PEC | Telefono */}
                <div className="grid md:grid-cols-3 gap-4 pb-4 border-b border-gray-100 dark:border-gray-800">
                  {/* Email principale + secondarie */}
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1 flex items-center gap-1">
                      <Mail className="w-3 h-3" /> Email
                    </label>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white break-all">{associate.email}</p>
                    {profileEmailSecondarie.map((em, i) => (
                      <div key={i} className="flex items-center gap-1 mt-1">
                        <span className="text-xs text-gray-700 dark:text-gray-300 break-all flex-1">{em}</span>
                        <button onClick={() => handleRemoveEmail(i)} className="p-0.5 text-red-400 hover:text-red-600 flex-shrink-0" title="Rimuovi">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    {newEmailInput !== null ? (
                      <div className="flex gap-1 mt-1">
                        <input
                          type="email"
                          value={newEmailInput}
                          onChange={e => setNewEmailInput(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleConfirmEmail()}
                          placeholder="email@esempio.it"
                          autoFocus
                          className="flex-1 min-w-0 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-blue-500 outline-none"
                        />
                        <button onClick={handleConfirmEmail} className="p-1 text-green-500 hover:text-green-700 flex-shrink-0" title="Conferma">
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => setNewEmailInput('')} className="mt-1 text-xs text-blue-500 hover:text-blue-700 dark:text-blue-400 hover:underline flex items-center gap-0.5">
                        <Plus className="w-3 h-3" /> aggiungi mail
                      </button>
                    )}
                  </div>

                  {/* PEC */}
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1 flex items-center gap-1">
                      <Mail className="w-3 h-3" /> PEC
                    </label>
                    {editingPec ? (
                      <div className="flex gap-1">
                        <input
                          type="email"
                          value={profilePec}
                          onChange={e => setProfilePec(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleConfirmPec()}
                          placeholder="pec@esempio.it"
                          autoFocus
                          className="flex-1 min-w-0 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-blue-500 outline-none"
                        />
                        <button onClick={handleConfirmPec} className="p-1 text-green-500 hover:text-green-700 flex-shrink-0" title="Conferma">
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : profilePec ? (
                      <div className="flex items-center gap-1">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white break-all flex-1">{profilePec}</span>
                        <button onClick={handleRemovePec} className="p-0.5 text-red-400 hover:text-red-600 flex-shrink-0" title="Rimuovi PEC">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => setEditingPec(true)} className="text-xs text-blue-500 hover:text-blue-700 dark:text-blue-400 hover:underline flex items-center gap-0.5">
                        <Plus className="w-3 h-3" /> aggiungi pec
                      </button>
                    )}
                  </div>

                  {/* Telefono principale + extra */}
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1 flex items-center gap-1">
                      <Phone className="w-3 h-3" /> Telefono
                    </label>
                    {associate.telefono
                      ? <p className="text-sm font-semibold text-gray-900 dark:text-white">{associate.telefono}</p>
                      : <p className="text-sm text-gray-400">—</p>
                    }
                    {profileTelefoniExtra.map((tel, i) => (
                      <div key={i} className="flex items-center gap-1 mt-1">
                        <span className="text-xs text-gray-700 dark:text-gray-300 flex-1">{tel}</span>
                        <button onClick={() => handleRemoveTel(i)} className="p-0.5 text-red-400 hover:text-red-600 flex-shrink-0" title="Rimuovi">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    {newTelInput !== null ? (
                      <div className="flex gap-1 mt-1">
                        <input
                          type="tel"
                          value={newTelInput}
                          onChange={e => setNewTelInput(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleConfirmTel()}
                          placeholder="+39 000 0000000"
                          autoFocus
                          className="flex-1 min-w-0 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-blue-500 outline-none"
                        />
                        <button onClick={handleConfirmTel} className="p-1 text-green-500 hover:text-green-700 flex-shrink-0" title="Conferma">
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => setNewTelInput('')} className="mt-1 text-xs text-blue-500 hover:text-blue-700 dark:text-blue-400 hover:underline flex items-center gap-0.5">
                        <Plus className="w-3 h-3" /> aggiungi tel.
                      </button>
                    )}
                  </div>
                </div>

                {/* ===== ATTIVITÀ SVOLTE ===== */}
                {allActivities.length > 0 && (() => {
                  // Categorie visibili in base a categoria principale + integrative di settore
                  const categoriaBase = associate.categoria?.toUpperCase() || '';
                  const integrative = (associate.integrativa || '').toUpperCase().split(' ').filter(Boolean);
                  const visibiliCat = new Set<string>(['TUTTI', categoriaBase, ...integrative]);
                  const activitiesFiltrate = allActivities.filter(a => visibiliCat.has(a.categoria));

                  return (
                  <div className="pb-4 border-b border-gray-100 dark:border-gray-800">
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <CheckSquare className="w-3 h-3" />
                        Attività svolte nel mio studio
                      </label>
                      {!editingProfile ? (
                        <button
                          onClick={() => setEditingProfile(true)}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                        >
                          <Pencil className="w-3 h-3" /> Modifica
                        </button>
                      ) : (
                        <div className="flex gap-2">
                          <button
                            onClick={() => setEditingProfile(false)}
                            className="text-xs text-gray-500 hover:underline"
                          >Annulla</button>
                          <button
                            onClick={async () => { await handleSaveProfile(); setEditingProfile(false); }}
                            disabled={savingProfile}
                            className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                          >
                            <Save className="w-3 h-3" />
                            {savingProfile ? 'Salvo...' : 'Salva'}
                          </button>
                        </div>
                      )}
                    </div>
                    {editingProfile ? (
                      <div className="space-y-1">
                        {(['STUDIO', 'AUTOSCUOLA', 'NAUTICA', 'TUTTI'] as const).map(cat => {
                          const group = activitiesFiltrate.filter(a => a.categoria === cat);
                          if (!group.length) return null;
                          return (
                            <div key={cat} className="mb-2">
                              <span className={`inline-block px-1.5 py-0.5 text-xs font-medium rounded mb-1 ${CATEGORIE_BADGE[cat]}`}>{cat}</span>
                              <div className="flex flex-wrap gap-x-4 gap-y-1 ml-1">
                                {group.map(a => (
                                  <label key={a._id} className="inline-flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={profileAttivita.includes(a._id)}
                                      onChange={() => toggleAttivita(a._id)}
                                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    {a.nome}
                                  </label>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div>
                        {profile?.attivitaSvolte && profile.attivitaSvolte.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5">
                            {profile.attivitaSvolte.map((a: any) => (
                              <span key={a._id} className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${CATEGORIE_BADGE[a.categoria] || 'bg-gray-100 text-gray-600'}`}>
                                {a.nome}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400 italic">Nessuna attività selezionata — clicca Modifica per aggiungerne</p>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })()}

                {/* Indirizzo Completo */}
                <div className="pb-4 border-b border-gray-100 dark:border-gray-800">
                  <label className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1 flex items-center gap-1">
                    <Home className="w-3 h-3" />
                    Indirizzo Sede Legale
                  </label>
                  {associate.indirizzo || associate.civico ? (
                    <>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {[associate.indirizzo, associate.civico].filter(Boolean).join(', ')}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {[associate.cap, associate.citta].filter(Boolean).join(' - ')} {associate.provincia && `(${associate.provincia})`}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {associate.regione}
                      </p>
                    </>
                  ) : (
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      {[associate.citta, associate.provincia && `(${associate.provincia})`, associate.regione].filter(Boolean).join(', ')}
                    </p>
                  )}
                </div>

                {/* Grid 2 colonne per dati aggiuntivi */}
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Data Iscrizione - NASCOSTO durante tesseramento 2026
                  {associate.iscrizione && (
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Data Iscrizione
                      </label>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {new Date(associate.iscrizione).toLocaleDateString('it-IT')}
                      </p>
                    </div>
                  )}
                  */}

                  {/* Categoria Integrativa */}
                  {associate.integrativa && (
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1 flex items-center gap-1">
                        <Briefcase className="w-3 h-3" />
                        Categoria Integrativa
                      </label>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {associate.integrativa}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Card Password di accesso (alternativa all'OTP) */}
            {(() => {
              const authInfo = associate.auth;
              const hasPermanent = !!authInfo?.hasPassword && !authInfo?.temporary;
              const inputClass = 'w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none';
              return (
                <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-800 p-6 transition-shadow duration-200 hover:shadow-xl anim-fade-up" style={{ animationDelay: '120ms' }}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-md shadow-indigo-500/20">
                      <KeyRound className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-lg font-bold text-gray-900 dark:text-white">Password di accesso</h2>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {authInfo?.hasPassword
                          ? (authInfo.temporary
                            ? 'Stai usando una password temporanea: scegline una definitiva.'
                            : `Impostata${authInfo.passwordSetAt ? ' il ' + new Date(authInfo.passwordSetAt).toLocaleDateString('it-IT') : ''}. Puoi accedere anche senza codice via email.`)
                          : 'Non impostata. Con una password puoi entrare senza aspettare il codice via email.'}
                      </p>
                    </div>
                    {!pwdOpen && (
                      <button onClick={() => setPwdOpen(true)} className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700">
                        {authInfo?.hasPassword ? 'Cambia' : 'Imposta'}
                      </button>
                    )}
                  </div>

                  {authInfo?.temporary && (
                    <div className="mb-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-200">
                      La password temporanea scade dopo 48 ore. Imposta ora la tua password definitiva.
                    </div>
                  )}

                  {pwdOpen && (
                    <div className="space-y-3">
                      {hasPermanent && (
                        <input type={pwdShow ? 'text' : 'password'} value={pwdCurrent} onChange={e => setPwdCurrent(e.target.value)} placeholder="Password attuale" autoComplete="current-password" className={inputClass} />
                      )}
                      <input type={pwdShow ? 'text' : 'password'} value={pwdNew} onChange={e => setPwdNew(e.target.value)} placeholder="Nuova password (almeno 8 caratteri)" autoComplete="new-password" className={inputClass} />
                      <input type={pwdShow ? 'text' : 'password'} value={pwdConfirm} onChange={e => setPwdConfirm(e.target.value)} placeholder="Ripeti la nuova password" autoComplete="new-password" className={inputClass} />
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <button type="button" onClick={() => setPwdShow(v => !v)} className="text-xs text-gray-500 dark:text-gray-400 inline-flex items-center gap-1">
                          {pwdShow ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />} {pwdShow ? 'Nascondi' : 'Mostra'}
                        </button>
                        <div className="flex gap-2">
                          <button onClick={resetPwdForm} className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">Annulla</button>
                          <button onClick={handleSavePassword} disabled={pwdSaving} className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                            {pwdSaving ? 'Salvataggio…' : 'Salva password'}
                          </button>
                        </div>
                      </div>
                      {hasPermanent && (
                        <button onClick={() => setPwdRemoveOpen(true)} className="text-xs text-red-500 hover:text-red-700 hover:underline">
                          Rimuovi la password e usa solo il codice via email
                        </button>
                      )}
                    </div>
                  )}

                  <ConfirmDialog
                    isOpen={pwdRemoveOpen}
                    onClose={() => setPwdRemoveOpen(false)}
                    onConfirm={handleRemovePassword}
                    type="warning"
                    title="Rimuovere la password?"
                    message="Da quel momento potrai entrare solo con il codice via email. Serve la password attuale inserita nel campo qui sopra."
                    confirmText="Rimuovi"
                  />
                </div>
              );
            })()}

            {/* Card I miei dipendenti (solo titolare; assente se la funzione non è attiva per lo studio) */}
            <GestioneDipendenti />

            {/* Card Posizione Sedi nella Mappa */}
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-800 p-6 transition-shadow duration-200 hover:shadow-xl anim-fade-up" style={{ animationDelay: '160ms' }}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center shadow-md shadow-green-500/20">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                    Posizione sedi nella mappa della Rete Certificata
                  </h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {sedi.length > 1 ? 'Seleziona una sede per visualizzarla sulla mappa' : 'La tua sede sulla mappa'}
                  </p>
                </div>
              </div>

              {/* Loading state */}
              {loadingSedi && (
                <div className="flex items-center justify-center h-48 bg-gray-50 dark:bg-gray-800 rounded-xl">
                  <div className="w-8 h-8 relative mx-auto">
                    <div className="absolute inset-0 rounded-full border-2 border-green-200 dark:border-green-900"></div>
                    <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-green-500 animate-spin"></div>
                  </div>
                </div>
              )}

              {/* Error state */}
              {sediError && !loadingSedi && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300 text-sm">
                  {sediError}
                </div>
              )}

              {/* Mappa con sede selezionata */}
              {!loadingSedi && !sediError && sedi.length > 0 && sedi[selectedSedeIndex] && (() => {
                const selectedSede = sedi[selectedSedeIndex];
                const coords = selectedSede.coordinates;
                return (
                  <AssociateLocationMap
                    key={selectedSede.id}
                    sedeId={String(selectedSede.id)}
                    onInviata={(id, punto) => setSedi(prev => prev.map(s => (String(s.id) === id
                      ? { ...s, coordinates: { ...(s.coordinates || {}), lat: punto.lat, lng: punto.lng } }
                      : s)))}
                    associate={{
                      nominativo: selectedSede.nome,
                      partitaIVA: associate.partitaIVA,
                      email: selectedSede.email || associate.email,
                      telefono: selectedSede.telefono || associate.telefono,
                      indirizzo: selectedSede.indirizzo,
                      civico: selectedSede.civico,
                      cap: selectedSede.cap,
                      citta: selectedSede.citta || '',
                      provincia: selectedSede.provincia || '',
                      regione: selectedSede.regione || '',
                      coordinates: coords ? {
                        lat: coords.lat,
                        lng: coords.lng,
                        geocoded: coords.geocoded,
                        geocodeQuality: coords.geocodeQuality as any
                      } : undefined
                    }}
                  />
                );
              })()}

              {/* Card sedi sotto la mappa */}
              {!loadingSedi && sedi.length > 1 && (
                <div className="mt-6 space-y-3">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    Le tue sedi ({sedi.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {sedi.map((sede, index) => (
                      <div
                        key={sede.id}
                        onClick={() => setSelectedSedeIndex(index)}
                        className={`p-4 rounded-lg border text-left transition-all cursor-pointer ${
                          selectedSedeIndex === index
                            ? 'bg-green-50 dark:bg-green-900/20 border-green-500 ring-2 ring-green-500/30'
                            : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-green-300 dark:hover:border-green-700'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {sede.isPrincipal ? (
                              <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded font-medium">
                                Principale
                              </span>
                            ) : (
                              <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded">
                                Sede {index + 1}
                              </span>
                            )}
                            {sede.coordinates?.geocoded && (
                              <MapPin className="w-3.5 h-3.5 text-green-500" />
                            )}
                            {!sede.isPrincipal && (
                              <SedePagamentoBadge quoteIntegrative={sede.quoteIntegrative} isPrincipal={sede.isPrincipal} />
                            )}
                          </div>
                          {selectedSedeIndex === index && (
                            <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                              Visualizzata
                            </span>
                          )}
                        </div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {sede.nome}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                          {sede.indirizzoCompleto}
                        </p>

                        {/* Form modifica contatti per sede secondaria */}
                        {!sede.isPrincipal && editingSedeContact === sede.id ? (
                          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600" onClick={(e) => e.stopPropagation()}>
                            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Modifica contatti sede:</p>
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <Phone className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                <input
                                  type="tel"
                                  placeholder="Telefono"
                                  value={sedeContactForm.telefono}
                                  onChange={(e) => setSedeContactForm({...sedeContactForm, telefono: e.target.value})}
                                  className="flex-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                              </div>
                              <div className="flex items-center gap-2">
                                <Mail className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                <input
                                  type="email"
                                  placeholder="Email"
                                  value={sedeContactForm.email}
                                  onChange={(e) => setSedeContactForm({...sedeContactForm, email: e.target.value})}
                                  className="flex-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                              </div>
                              <div className="flex gap-2 mt-2">
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleSaveSedeContact(); }}
                                  disabled={savingSedeContact}
                                  className="flex-1 px-2 py-1.5 text-xs bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded flex items-center justify-center gap-1"
                                >
                                  <Save className="w-3 h-3" />
                                  {savingSedeContact ? 'Salvo...' : 'Salva'}
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setEditingSedeContact(null); }}
                                  className="px-2 py-1.5 text-xs bg-gray-500 hover:bg-gray-600 text-white rounded"
                                >
                                  Annulla
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <>
                            {/* Contatti visualizzati o messaggio per aggiungere */}
                            {(sede.telefono || sede.email) ? (
                              <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 space-y-1">
                                {sede.telefono && (
                                  <p className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1">
                                    <Phone className="w-3 h-3" /> {sede.telefono}
                                  </p>
                                )}
                                {sede.email && (
                                  <p className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1 truncate">
                                    <Mail className="w-3 h-3" /> {sede.email}
                                  </p>
                                )}
                              </div>
                            ) : !sede.isPrincipal && (
                              <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                                <p className="text-xs text-amber-600 dark:text-amber-400 italic">
                                  Contatti non inseriti
                                </p>
                              </div>
                            )}

                            {/* Bottone modifica per sedi secondarie */}
                            {!sede.isPrincipal && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleEditSedeContact(sede); }}
                                className="mt-2 w-full px-2 py-1.5 text-xs bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded border border-blue-200 dark:border-blue-800 flex items-center justify-center gap-1"
                              >
                                <Pencil className="w-3 h-3" />
                                {sede.telefono || sede.email ? 'Modifica contatti' : 'Aggiungi contatti'}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Colonna Destra - Tesseramento e Rete */}
          <div className="space-y-6">
            {/* Tesseramento */}
            <AssociateFeeCard
              associate={associate}
              onAssociateRefresh={async () => {
                try {
                  const freshData = await refreshAssociateData();
                  setAssociate(freshData);
                  // Riallinea il sollecito pagamenti sopra le schede
                  try {
                    const payRes = await getPaymentStatus();
                    setPaymentStatus(payRes.data);
                  } catch { /* silenzioso */ }
                } catch (err) {
                  console.error('Errore refresh dati:', err);
                }
              }}
            />

            {/* Rete Certificata */}
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-800 p-6 transition-shadow duration-200 hover:shadow-xl anim-fade-up" style={{ animationDelay: '200ms' }}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center shadow-md shadow-amber-500/20">
                  <Award className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                  Rete Certificata
                </h2>
              </div>

              {loadingStatus ? (
                <div className="flex justify-center py-4">
                  <div className="w-8 h-8 relative">
                    <div className="absolute inset-0 rounded-full border-2 border-amber-200 dark:border-amber-900"></div>
                    <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-amber-500 animate-spin"></div>
                  </div>
                </div>
              ) : certifiedNetworkStatus ? (
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1 block">Stato Iscrizione</label>
                    <div className="flex items-center gap-2 mt-2">
                      {certifiedNetworkStatus.isMember ? (
                        <>
                          {certifiedNetworkStatus.isActive ? (
                            <>
                              <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                              <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                                {certifiedNetworkStatus.message}
                              </span>
                            </>
                          ) : (
                            <>
                              <XCircle className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                              <span className="text-sm font-semibold text-orange-600 dark:text-orange-400">
                                {certifiedNetworkStatus.message}
                              </span>
                            </>
                          )}
                        </>
                      ) : certifiedNetworkStatus.hasApplication ? (
                        <>
                          <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                          <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                            {certifiedNetworkStatus.message}
                          </span>
                        </>
                      ) : (
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                            <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                              Non sei ancora iscritto alla Rete Certificata
                            </span>
                          </div>
                          {certifiedNetworkStatus.canApply && (
                            <a
                              href="/rete-certificata/iscrizione"
                              className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline transition-colors"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                              Iscriviti!
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Attività Certificate/Preautorizzate */}
                  {certifiedNetworkStatus.isMember && certifiedNetworkStatus.certifiedActivities && certifiedNetworkStatus.certifiedActivities.length > 0 && (
                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2 block">
                        Attività Certificate
                      </label>
                      <div className="flex flex-wrap gap-1.5">
                        {certifiedNetworkStatus.certifiedActivities.map((activity, idx) => (
                          <span
                            key={idx}
                            className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
                              activity.isCertified === 'SI'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                            }`}
                            title={activity.name}
                          >
                            {activity.isCertified === 'SI' ? '✓' : '⏳'} {activity.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Sezione Pagamento - Solo per membri certificati */}
                  {certifiedNetworkStatus.isMember && certifiedNetworkStatus.certificateHistory.length > 0 && (
                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                      <CertifiedNetworkPayment
                        certificateHistory={certifiedNetworkStatus.certificateHistory}
                        onPaymentSuccess={handlePaymentSuccess}
                        currentCertificateStatus={certifiedNetworkStatus.currentCertificateStatus}
                      />
                    </div>
                  )}

                  {/* Certificate info */}
                  {certifiedNetworkStatus.certificateNumber && (
                    <div className="mt-3 pt-3 border-t dark:border-gray-700">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        N. Certificato: <strong className="text-gray-900 dark:text-gray-100">{certifiedNetworkStatus.certificateNumber}</strong>
                      </p>
                      {certifiedNetworkStatus.certificateScannedPdf ? (
                        <button
                          onClick={handleDownloadCertificate}
                          className="mt-2 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Scarica Certificato
                        </button>
                      ) : (
                        <p className="mt-1 text-xs text-gray-400 italic">Certificato in fase di emissione</p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">Caricamento...</p>
              )}
            </div>

            {/* Codice STA e Matricola */}
            <STACodeCard />
          </div>
        </div>
        )}

        {/* —— SCHEDA: Servizi e Azioni —— */}
        {(activeTab === 'servizi' || soloFacile) && (
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-800 p-6 transition-shadow duration-200 hover:shadow-xl anim-fade-up" style={{ animationDelay: '0ms' }} role="tabpanel" id="panel-servizi" aria-labelledby="tab-servizi">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-md shadow-indigo-500/20">
              <Briefcase className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              Servizi e Azioni
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {soloFacile ? cardFacile : dipendente && studioNonInRegola ? (
              <AvvisoStudioNonInRegola className="sm:col-span-2 lg:col-span-4" />
            ) : (
            <>
            {dipendenteSenzaCard && (
              <p className="sm:col-span-2 lg:col-span-4 text-sm text-gray-500 dark:text-gray-400">
                Il titolare dello studio non ti ha ancora abilitato nessun servizio.
              </p>
            )}
            {/* Verifica del telaio: è l'azione principale dello strumento Takata, per questo
                sta prima della raccolta. In beta: funziona ed è usata in produzione, ma i
                portali dei costruttori cambiano spesso e qualche marca può dare problemi.
                Ogni card resta con le condizioni di oggi; al dipendente si mostra solo se la
                sezione gli è abilitata (sezioneVisibile è sempre vera per il titolare). */}
            {sezioneVisibile(associate, 'takata') && (
            <button
              onClick={handleVerificaTakataClick}
              className={`group relative rounded-xl p-5 transition-all duration-300 text-left overflow-hidden border-2 ${
                isInRegolaQuota
                  ? 'bg-gradient-to-br from-red-50 to-orange-100 dark:from-red-900/20 dark:to-orange-800/20 border-red-200 dark:border-red-700 hover:shadow-xl hover:scale-105 cursor-pointer'
                  : 'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800/40 dark:to-gray-700/40 border-gray-300 dark:border-gray-600 opacity-50 grayscale cursor-not-allowed'
              }`}
            >
              <div className="absolute top-3 right-3 z-10">
                {isInRegolaQuota ? (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/20 text-amber-700 dark:text-amber-400">
                    Beta
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-500/80 text-white shadow-md">
                    <Lock className="w-3 h-3" /> Quota
                  </span>
                )}
              </div>
              <div className="absolute top-0 right-0 w-20 h-20 bg-red-500/10 rounded-full -mr-10 -mt-10"></div>
              <div className="relative">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 shadow-lg ${
                  isInRegolaQuota
                    ? 'bg-gradient-to-br from-red-500 to-orange-600 group-hover:scale-110 transition-transform'
                    : 'bg-gradient-to-br from-gray-400 to-gray-500'
                }`}>
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <p className={`text-sm font-bold mb-1 ${isInRegolaQuota ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>Verifica richiamo Takata</p>
                <p className={`text-xs ${isInRegolaQuota ? 'text-gray-600 dark:text-gray-400' : 'text-gray-400 dark:text-gray-500'}`}>Dal telaio al certificato, con la prova allegata</p>
              </div>
            </button>
            )}

            {/* La card «Raccolta telai Takata» è stata TOLTA il 30/07/2026: la raccolta è
                servita a tarare le formule d'esito e non serve più — i telai ora arrivano
                dalle verifiche vere. La pagina /area-riservata/takata/raccolta resta
                raggiungibile per chi ha il collegamento, solo non viene più proposta. */}

            {/* Rimborso Voucher PagoPA - Solo Rete Certificata */}
            {sezioneVisibile(associate, 'voucher') && (
            <button
              onClick={(e) => isCertifiedActive ? navigate('/area-riservata/rimborso-voucher') : handleProtectedLinkClick('/area-riservata/rimborso-voucher', e)}
              className={`group relative rounded-xl p-5 transition-all duration-300 text-left overflow-hidden border-2 ${
                isCertifiedActive
                  ? 'bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200 dark:border-green-700 hover:shadow-xl hover:scale-105 cursor-pointer'
                  : 'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800/40 dark:to-gray-700/40 border-gray-300 dark:border-gray-600 opacity-50 grayscale cursor-not-allowed'
              }`}
            >
              <div className="absolute top-3 right-3 z-10">
                {isCertifiedActive ? (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-green-500/15 text-green-700 dark:text-green-400">
                    Rete Certificata
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-500/80 text-white shadow-md">
                    <Lock className="w-3 h-3" /> Rete Certificata
                  </span>
                )}
              </div>
              <div className="absolute top-0 right-0 w-20 h-20 bg-green-500/10 rounded-full -mr-10 -mt-10"></div>
              <div className="relative">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 shadow-lg ${
                  isCertifiedActive
                    ? 'bg-gradient-to-br from-green-500 to-green-600 group-hover:scale-110 transition-transform'
                    : 'bg-gradient-to-br from-gray-400 to-gray-500'
                }`}>
                  <Receipt className="w-6 h-6 text-white" />
                </div>
                <p className={`text-sm font-bold mb-1 ${isCertifiedActive ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>Scambio Voucher</p>
                <p className={`text-xs ${isCertifiedActive ? 'text-gray-600 dark:text-gray-400' : 'text-gray-400 dark:text-gray-500'}`}>Gestione rimborsi/scambio PagoPA</p>
              </div>
            </button>
            )}

            {/* Svincolo atti di vendita - Solo Rete Certificata */}
            {sezioneVisibile(associate, 'svincolo') && (
            <button
              onClick={(e) => isCertifiedActive ? navigate('/area-riservata/svincolo-atti/lista') : handleProtectedLinkClick('/area-riservata/svincolo-atti/lista', e)}
              className={`group relative rounded-xl p-5 transition-all duration-300 text-left overflow-hidden border-2 ${
                isCertifiedActive
                  ? 'bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200 dark:border-purple-700 hover:shadow-xl hover:scale-105 cursor-pointer'
                  : 'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800/40 dark:to-gray-700/40 border-gray-300 dark:border-gray-600 opacity-50 grayscale cursor-not-allowed'
              }`}
            >
              <div className="absolute top-3 right-3 z-10">
                {isCertifiedActive ? (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-purple-500/15 text-purple-700 dark:text-purple-400">
                    Rete Certificata
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-500/80 text-white shadow-md">
                    <Lock className="w-3 h-3" /> Rete Certificata
                  </span>
                )}
              </div>
              <div className="absolute top-0 right-0 w-20 h-20 bg-purple-500/10 rounded-full -mr-10 -mt-10"></div>
              <div className="relative">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 shadow-lg ${
                  isCertifiedActive
                    ? 'bg-gradient-to-br from-purple-500 to-purple-600 group-hover:scale-110 transition-transform'
                    : 'bg-gradient-to-br from-gray-400 to-gray-500'
                }`}>
                  <FileCheck className="w-6 h-6 text-white" />
                </div>
                <p className={`text-sm font-bold mb-1 ${isCertifiedActive ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>Svincolo atti di vendita</p>
                <p className={`text-xs ${isCertifiedActive ? 'text-gray-600 dark:text-gray-400' : 'text-gray-400 dark:text-gray-500'}`}>Gestisci richieste svincolo atti</p>
              </div>
            </button>
            )}

            {/* Collabora con altri membri - Solo Rete Certificata */}
            {sezioneVisibile(associate, 'collabora') && (
            <button
              onClick={(e) => isCertifiedActive ? navigate('/area-riservata/collabora') : handleProtectedLinkClick('/area-riservata/collabora', e)}
              className={`group relative rounded-xl p-5 transition-all duration-300 text-left overflow-hidden border-2 ${
                isCertifiedActive
                  ? 'bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 border-amber-200 dark:border-amber-700 hover:shadow-xl hover:scale-105 cursor-pointer'
                  : 'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800/40 dark:to-gray-700/40 border-gray-300 dark:border-gray-600 opacity-50 grayscale cursor-not-allowed'
              }`}
            >
              <div className="absolute top-3 right-3 z-10">
                {isCertifiedActive ? (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-500/15 text-amber-700 dark:text-amber-400">
                    Rete Certificata
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-500/80 text-white shadow-md">
                    <Lock className="w-3 h-3" /> Rete Certificata
                  </span>
                )}
              </div>
              <div className="absolute top-0 right-0 w-20 h-20 bg-amber-500/10 rounded-full -mr-10 -mt-10"></div>
              <div className="relative">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 shadow-lg ${
                  isCertifiedActive
                    ? 'bg-gradient-to-br from-amber-500 to-amber-600 group-hover:scale-110 transition-transform'
                    : 'bg-gradient-to-br from-gray-400 to-gray-500'
                }`}>
                  <Handshake className="w-6 h-6 text-white" />
                </div>
                <p className={`text-sm font-bold mb-1 ${isCertifiedActive ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>Collabora con altri membri</p>
                <p className={`text-xs ${isCertifiedActive ? 'text-gray-600 dark:text-gray-400' : 'text-gray-400 dark:text-gray-500'}`}>Contatta colleghi per pratiche e collaborazioni</p>
              </div>
            </button>
            )}

            {/* Partnership Facile.it - Solo Rete Certificata */}
            {sezioneVisibile(associate, 'facile') && cardFacile}

            {/* I miei eventi - accessibile a tutti */}
            {sezioneVisibile(associate, 'eventi') && (
            <button
              onClick={() => navigate('/area-riservata/eventi')}
              className="group relative bg-gradient-to-br from-rose-50 to-rose-100 dark:from-rose-900/20 dark:to-rose-800/20 border-2 border-rose-200 dark:border-rose-700 rounded-xl p-5 hover:shadow-xl hover:scale-105 transition-all duration-300 text-left overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-20 h-20 bg-rose-500/10 rounded-full -mr-10 -mt-10"></div>
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-rose-500 to-rose-600 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg">
                  <QrCode className="w-6 h-6 text-white" />
                </div>
                <p className="text-sm font-bold text-gray-900 dark:text-white mb-1">I miei eventi</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Inviti e QR code check-in</p>
              </div>
            </button>
            )}

            {/* Sondaggi */}
            {sezioneVisibile(associate, 'sondaggi') && (
            <button
              onClick={() => navigate('/area-riservata/sondaggi')}
              className="group relative bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 border-2 border-indigo-200 dark:border-indigo-700 rounded-xl p-5 hover:shadow-xl hover:scale-105 transition-all duration-300 text-left overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-20 h-20 bg-indigo-500/10 rounded-full -mr-10 -mt-10"></div>
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg">
                  <ClipboardList className="w-6 h-6 text-white" />
                </div>
                <p className="text-sm font-bold text-gray-900 dark:text-white mb-1">Sondaggi</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Partecipa ai sondaggi UNASCA</p>
              </div>
            </button>
            )}

            {/* Supporto Prioritario  
            <button
              onClick={handleComingSoonClick}
              className="group relative bg-gradient-to-br from-rose-50 to-rose-100 dark:from-rose-900/20 dark:to-rose-800/20 border-2 border-rose-200 dark:border-rose-700 rounded-xl p-5 hover:shadow-xl hover:scale-105 transition-all duration-300 text-left overflow-hidden"
            >
              <div className="absolute top-3 right-3 z-10">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-400 text-yellow-900 shadow-md">
                  In arrivo
                </span>
              </div>
              <div className="absolute top-0 right-0 w-20 h-20 bg-rose-500/10 rounded-full -mr-10 -mt-10"></div>
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-rose-500 to-rose-600 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg">
                  <Headphones className="w-6 h-6 text-white" />
                </div>
                <p className="text-sm font-bold text-gray-900 dark:text-white mb-1">Supporto Prioritario</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Assistenza dedicata con priorità alta</p>
              </div>
            </button>
            **/}
            
            {/* Centro Studi Cesare Ferrari — card nascosta su richiesta
            <button
              onClick={handleComingSoonClick}
              className="group relative bg-gradient-to-br from-teal-50 to-teal-100 dark:from-teal-900/20 dark:to-teal-800/20 border-2 border-teal-200 dark:border-teal-700 rounded-xl p-5 hover:shadow-xl hover:scale-105 transition-all duration-300 text-left overflow-hidden"
            >
              <div className="absolute top-3 right-3 z-10">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-400 text-yellow-900 shadow-md">
                  In arrivo
                </span>
              </div>
              <div className="absolute top-0 right-0 w-20 h-20 bg-teal-500/10 rounded-full -mr-10 -mt-10"></div>
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg">
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <p className="text-sm font-bold text-gray-900 dark:text-white mb-1">Centro Studi Cesare Ferrari</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Accedi all'area riservata</p>
              </div>
            </button>
            */}

            {/* Supporto — card unica del Support Hub: richieste ai gruppi tematici,
                segnalazioni/feedback e risposte. Il click apre la LISTA (chi ha una
                risposta non letta la trova subito); la modale si apre solo dal link
                secondario "Nuova richiesta". */}
            {sezioneVisibile(associate, 'supporto') && (
            <button
              onClick={() => navigate('/area-riservata/supporto')}
              className="group relative bg-gradient-to-br from-sky-50 to-sky-100 dark:from-sky-900/20 dark:to-sky-800/20 border-2 border-sky-200 dark:border-sky-700 rounded-xl p-5 hover:shadow-xl hover:scale-105 transition-all duration-300 text-left overflow-hidden"
            >
              {/* Badge risposte non lette */}
              {feedbackUnreadCount > 0 && (
                <div className="absolute top-2 right-2 min-w-[22px] h-[22px] bg-red-500 rounded-full flex items-center justify-center z-10 animate-pulse shadow-lg px-1" title={`${feedbackUnreadCount} risposte da leggere`}>
                  <span className="text-xs text-white font-bold">{feedbackUnreadCount}</span>
                </div>
              )}
              <div className="absolute top-0 right-0 w-20 h-20 bg-sky-500/10 rounded-full -mr-10 -mt-10"></div>
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-sky-500 to-sky-600 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg">
                  <LifeBuoy className="w-6 h-6 text-white" />
                </div>
                <p className="text-sm font-bold text-gray-900 dark:text-white mb-1">Supporto</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Richieste, segnalazioni e risposte</p>
                {/* Scorciatoia: apre direttamente la modale (span, non button: siamo già dentro un button) */}
                <span
                  role="link"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); navigate('/area-riservata/supporto?nuovo=1'); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); navigate('/area-riservata/supporto?nuovo=1'); } }}
                  className="inline-block mt-2 text-xs font-semibold text-sky-700 dark:text-sky-300 hover:underline"
                >
                  + Nuova richiesta
                </span>
              </div>
            </button>
            )}

            {/* Area Associativa - solo per rappresentanti con cariche E permesso di accesso (associativa.dashboard.read); mai al dipendente */}
            {!dipendente && repInfo?.hasRepresentative && repInfo.canAccessArea && (
              <button
                onClick={async () => {
                  // Se sospeso, mostra messaggio e non fa accedere
                  if (repInfo.status === 'sospeso') {
                    toast.error('Il tuo profilo associativo e temporaneamente sospeso. Contatta la segreteria per maggiori informazioni.', { duration: 5000 });
                    return;
                  }
                  if (repInfo.hasPin) {
                    setShowPinModal(true);
                  } else {
                    try {
                      const { verifyPin, saveRepresentativeToken } = await import('../services/representativeService');
                      const result = await verifyPin(repInfo.representativeId!);
                      if (result.success) {
                        saveRepresentativeToken(result.data.token);
                        navigate('/area-associativa');
                      }
                    } catch { toast.error('Errore accesso area associativa'); }
                  }
                }}
                className={`group relative rounded-xl p-5 hover:shadow-xl hover:scale-105 transition-all duration-300 text-left overflow-hidden border-2 ${
                  repInfo.status === 'sospeso'
                    ? 'bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20 border-yellow-300 dark:border-yellow-700'
                    : 'bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20 border-emerald-200 dark:border-emerald-700'
                }`}
              >
                {repInfo.status === 'sospeso' && (
                  <div className="absolute top-3 right-3 z-10">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-400 text-yellow-900 shadow-md">
                      Sospeso
                    </span>
                  </div>
                )}
                <div className="absolute top-0 right-0 w-20 h-20 bg-emerald-500/10 rounded-full -mr-10 -mt-10"></div>
                <div className="relative">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg ${
                    repInfo.status === 'sospeso'
                      ? 'bg-gradient-to-br from-yellow-500 to-yellow-600'
                      : 'bg-gradient-to-br from-emerald-500 to-emerald-600'
                  }`}>
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <p className="text-sm font-bold text-gray-900 dark:text-white mb-1">Area Associativa</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {repInfo.caricaPrincipale}
                    {(repInfo.caricheAttive || 0) > 1 && (
                      <span className="font-semibold text-emerald-700 dark:text-emerald-400">
                        {' '}+{(repInfo.caricheAttive as number) - 1} {(repInfo.caricheAttive as number) - 1 === 1 ? 'altra carica' : 'altre cariche'}
                      </span>
                    )}
                  </p>
                  {repInfo.status === 'attivo' && repInfo.hasPin && (
                    <div className="flex items-center gap-1 mt-2">
                      <Lock className="w-3 h-3 text-emerald-500" />
                      <span className="text-xs text-emerald-600 dark:text-emerald-400">Protetto da PIN</span>
                    </div>
                  )}
                </div>
              </button>
            )}

            {/* PIN Modal */}
            {showPinModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={(e) => e.target === e.currentTarget && setShowPinModal(false)}>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-80 shadow-2xl">
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-4">Inserisci PIN Area Associativa</h3>
                  <input
                    type="password"
                    value={pinInput}
                    onChange={(e) => { setPinInput(e.target.value); setPinError(''); }}
                    placeholder="PIN"
                    maxLength={10}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white text-sm focus:ring-2 focus:ring-emerald-500"
                    autoFocus
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter') {
                        try {
                          const { verifyPin, saveRepresentativeToken } = await import('../services/representativeService');
                          const result = await verifyPin(repInfo!.representativeId!, pinInput);
                          if (result.success) {
                            saveRepresentativeToken(result.data.token);
                            setShowPinModal(false);
                            navigate('/area-associativa');
                          }
                        } catch (err: any) { setPinError(err.response?.data?.error || 'PIN non corretto'); }
                      }
                    }}
                  />
                  {pinError && <p className="text-xs text-red-500 mt-1">{pinError}</p>}
                  <div className="flex gap-2 mt-4">
                    <button onClick={() => { setShowPinModal(false); setPinInput(''); setPinError(''); }}
                      className="flex-1 px-3 py-2 text-xs bg-gray-200 dark:bg-gray-600 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500">
                      Annulla
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          const { verifyPin, saveRepresentativeToken } = await import('../services/representativeService');
                          const result = await verifyPin(repInfo!.representativeId!, pinInput);
                          if (result.success) {
                            saveRepresentativeToken(result.data.token);
                            setShowPinModal(false);
                            navigate('/area-associativa');
                          }
                        } catch (err: any) { setPinError(err.response?.data?.error || 'PIN non corretto'); }
                      }}
                      className="flex-1 px-3 py-2 text-xs bg-emerald-500 text-white rounded-lg hover:bg-emerald-600">
                      Accedi
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Novità Piattaforma - Changelog (non è una sezione: al dipendente non si mostra) */}
            {!dipendente && (
            <button
              onClick={() => navigate('/changelog')}
              className="group relative bg-gradient-to-br from-blue-50 to-purple-100 dark:from-blue-900/20 dark:to-purple-800/20 border-2 border-blue-200 dark:border-blue-700 rounded-xl p-5 hover:shadow-xl hover:scale-105 transition-all duration-300 text-left overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-20 h-20 bg-blue-500/10 rounded-full -mr-10 -mt-10"></div>
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <p className="text-sm font-bold text-gray-900 dark:text-white mb-1">Changelog - novità piattaforma</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Scopri tutte le nuove funzionalità rilasciate e sulle quali stiamo lavorando</p>
              </div>
            </button>
            )}

            {/* CTA Rete Certificata - ultima card, solo per non-membri (l'iscrizione è del titolare) */}
            {!isCertifiedActive && !dipendente && (
            <button
              onClick={() => navigate('/rete-certificata/studi')}
              className="cta-certified group relative bg-gradient-to-br from-blue-600 to-indigo-700 dark:from-blue-700 dark:to-indigo-800 border-2 border-blue-400/60 dark:border-blue-500/60 rounded-xl p-5 hover:shadow-2xl hover:scale-105 transition-all duration-300 text-left overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12"></div>
              <div className="relative">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg border border-white/30">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <p className="text-sm font-bold text-white mb-1">Non sei ancora nella Rete Certificata?</p>
                <p className="text-xs text-blue-100">Scopri i vantaggi e iscriviti subito</p>
              </div>
            </button>
            )}

            {/* Contattaci
            <button
              onClick={() => navigate('/contact')}
              className="group relative bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 border-2 border-orange-200 dark:border-orange-700 rounded-xl p-5 hover:shadow-xl hover:scale-105 transition-all duration-300 text-left overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-20 h-20 bg-orange-500/10 rounded-full -mr-10 -mt-10"></div>
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <p className="text-sm font-bold text-gray-900 dark:text-white mb-1">Contattaci</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Assistenza e supporto</p>
              </div>
            </button>
            */}

            {/* Circolari: archivio riservato agli associati in regola con la quota */}
            {!sezioneVisibile(associate, 'circolari') ? null : CIRCOLARI_IN_ARRIVO ? (
            <button
              onClick={handleComingSoonClick}
              className="group relative bg-gradient-to-br from-sky-50 to-sky-100 dark:from-sky-900/20 dark:to-sky-800/20 border-2 border-sky-200 dark:border-sky-700 rounded-xl p-5 hover:shadow-xl hover:scale-105 transition-all duration-300 text-left overflow-hidden"
            >
              <div className="absolute top-3 right-3 z-10">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-400 text-yellow-900 shadow-md">
                  In arrivo
                </span>
              </div>
              <div className="absolute top-0 right-0 w-20 h-20 bg-sky-500/10 rounded-full -mr-10 -mt-10"></div>
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-sky-500 to-sky-600 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <p className="text-sm font-bold text-gray-900 dark:text-white mb-1">Comunicati e norme</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Circolari, comunicati e normativa UNASCA</p>
              </div>
            </button>
            ) : (
            <button
              onClick={(e) => {
                if (isInRegolaCircolari) {
                  navigate('/area-riservata/circolari');
                } else {
                  e.preventDefault();
                  toast.error('Le circolari sono riservate agli associati in regola con la quota associativa.');
                }
              }}
              className={`group relative rounded-xl p-5 transition-all duration-300 text-left overflow-hidden border-2 ${
                isInRegolaCircolari
                  ? 'bg-gradient-to-br from-sky-50 to-sky-100 dark:from-sky-900/20 dark:to-sky-800/20 border-sky-200 dark:border-sky-700 hover:shadow-xl hover:scale-105 cursor-pointer'
                  : 'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800/40 dark:to-gray-700/40 border-gray-300 dark:border-gray-600 opacity-50 grayscale cursor-not-allowed'
              }`}
            >
              {!isInRegolaCircolari && (
                <div className="absolute top-3 right-3 z-10">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-500/80 text-white shadow-md">
                    <Lock className="w-3 h-3" /> Quota
                  </span>
                </div>
              )}
              <div className="absolute top-0 right-0 w-20 h-20 bg-sky-500/10 rounded-full -mr-10 -mt-10"></div>
              <div className="relative">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 shadow-lg ${
                  isInRegolaCircolari
                    ? 'bg-gradient-to-br from-sky-500 to-sky-600 group-hover:scale-110 transition-transform'
                    : 'bg-gradient-to-br from-gray-400 to-gray-500'
                }`}>
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <p className={`text-sm font-bold mb-1 ${isInRegolaCircolari ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>Comunicati e norme</p>
                <p className={`text-xs ${isInRegolaCircolari ? 'text-gray-600 dark:text-gray-400' : 'text-gray-400 dark:text-gray-500'}`}>Circolari, comunicati e normativa UNASCA</p>
              </div>
            </button>
            )}

            {/* I tuoi badge: codice da inserire nel sito dello studio (solo in regola; mai al dipendente) */}
            {!dipendente && (
            <button
              onClick={(e) => {
                if (isInRegolaCircolari) {
                  navigate('/area-riservata/badge');
                } else {
                  e.preventDefault();
                  toast.error('I badge sono riservati agli associati in regola con la quota associativa.');
                }
              }}
              className={`group relative rounded-xl p-5 transition-all duration-300 text-left overflow-hidden border-2 ${
                isInRegolaCircolari
                  ? 'bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200 dark:border-green-700 hover:shadow-xl hover:scale-105 cursor-pointer'
                  : 'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800/40 dark:to-gray-700/40 border-gray-300 dark:border-gray-600 opacity-50 grayscale cursor-not-allowed'
              }`}
            >
              {!isInRegolaCircolari && (
                <div className="absolute top-3 right-3 z-10">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-500/80 text-white shadow-md">
                    <Lock className="w-3 h-3" /> Quota
                  </span>
                </div>
              )}
              <div className="absolute top-0 right-0 w-20 h-20 bg-green-500/10 rounded-full -mr-10 -mt-10"></div>
              <div className="relative">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 shadow-lg ${
                  isInRegolaCircolari
                    ? 'bg-gradient-to-br from-green-600 to-green-700 group-hover:scale-110 transition-transform'
                    : 'bg-gradient-to-br from-gray-400 to-gray-500'
                }`}>
                  <BadgeCheck className="w-6 h-6 text-white" />
                </div>
                <p className={`text-sm font-bold mb-1 ${isInRegolaCircolari ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>I tuoi badge</p>
                <p className={`text-xs ${isInRegolaCircolari ? 'text-gray-600 dark:text-gray-400' : 'text-gray-400 dark:text-gray-500'}`}>Il badge UNASCA da mettere sul sito del tuo studio</p>
              </div>
            </button>
            )}

            {/* Chat associati: si mostra da sé solo se la chat è attiva per l'associato */}
            {sezioneVisibile(associate, 'chat') && <CardChatDashboard />}

            {/* ===== Servizi "In arrivo" — sempre in coda ===== */}

            {/* Community Rete Certificata - Solo Rete Certificata + In arrivo (la community è del titolare) */}
            {!dipendente && (
            <button
              onClick={(e) => !isCertifiedActive ? handleProtectedLinkClick('', e) : handleComingSoonClick(e)}
              className={`group relative rounded-xl p-5 transition-all duration-300 text-left overflow-hidden border-2 ${
                isCertifiedActive
                  ? 'bg-gradient-to-br from-cyan-50 to-cyan-100 dark:from-cyan-900/20 dark:to-cyan-800/20 border-cyan-200 dark:border-cyan-700 hover:shadow-xl hover:scale-105 cursor-pointer'
                  : 'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800/40 dark:to-gray-700/40 border-gray-300 dark:border-gray-600 opacity-50 grayscale cursor-not-allowed'
              }`}
            >
              <div className="absolute top-3 right-3 z-10 flex flex-col items-end gap-1">
                {isCertifiedActive ? (
                  <>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-400 text-yellow-900 shadow-md">
                      In arrivo
                    </span>
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-cyan-500/15 text-cyan-700 dark:text-cyan-400">
                      Rete Certificata
                    </span>
                  </>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-500/80 text-white shadow-md">
                    <Lock className="w-3 h-3" /> Rete Certificata
                  </span>
                )}
              </div>
              <div className="absolute top-0 right-0 w-20 h-20 bg-cyan-500/10 rounded-full -mr-10 -mt-10"></div>
              <div className="relative">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 shadow-lg ${
                  isCertifiedActive
                    ? 'bg-gradient-to-br from-cyan-500 to-cyan-600 group-hover:scale-110 transition-transform'
                    : 'bg-gradient-to-br from-gray-400 to-gray-500'
                }`}>
                  <MessageCircle className="w-6 h-6 text-white" />
                </div>
                <p className={`text-sm font-bold mb-1 ${isCertifiedActive ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>Community Rete Certificata</p>
                <p className={`text-xs ${isCertifiedActive ? 'text-gray-600 dark:text-gray-400' : 'text-gray-400 dark:text-gray-500'}`}>Confrontati con i colleghi</p>
              </div>
            </button>
            )}

            {/* Consorzio Sermetra — nascosta su richiesta (2026-10-07), il codice resta: togliere `false &&` per rimostrarla */}
            {false && (
            <button
              onClick={handleComingSoonClick}
              className="group relative bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 border-2 border-indigo-200 dark:border-indigo-700 rounded-xl p-5 hover:shadow-xl hover:scale-105 transition-all duration-300 text-left overflow-hidden"
            >
              <div className="absolute top-3 right-3 z-10">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-400 text-yellow-900 shadow-md">
                  In arrivo
                </span>
              </div>
              <div className="absolute top-0 right-0 w-20 h-20 bg-indigo-500/10 rounded-full -mr-10 -mt-10"></div>
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg">
                  <Building className="w-6 h-6 text-white" />
                </div>
                <p className="text-sm font-bold text-gray-900 dark:text-white mb-1">Consorzio Sermetra</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Iscriviti o accedi ai servizi del consorzio</p>
              </div>
            </button>
            )}
            </>
            )}

          </div>
        </div>
        )}
      </div>

      {/* Modale Partnership Facile.it */}
      {showFacileModal && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowFacileModal(false)}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-2xl w-full overflow-hidden max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header Modale */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <img
                  src="/images/logo-facile-it.jpg"
                  alt="Logo Facile.it"
                  className="w-10 h-10 rounded-lg object-cover shadow"
                />
                <div>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white">
                    Partnership Facile.it
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                    Opportunità per gli Studi della Rete Certificata
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowFacileModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            {/* Contenuto */}
            <div className="p-4 sm:p-6 space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Gli aderenti alla Rete Certificata potranno beneficiare di una serie di opportunità concrete:
              </p>

              <div className="flex gap-3 p-4 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-800">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center shadow-md">
                  <FileCheck className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900 dark:text-white mb-0.5">Passaggi di proprietà e rinnovi patente</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    I clienti intercettati online potranno prenotare appuntamenti presso gli Studi aderenti, che gestiranno direttamente la pratica fino alla conclusione.
                  </p>
                </div>
              </div>

              <div className="flex gap-3 p-4 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-800">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center shadow-md">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900 dark:text-white mb-0.5">Verifica del veicolo</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Uno strumento innovativo che consente di offrire controlli e verifiche preventive sui veicoli, aumentando il valore della consulenza fornita al cliente.
                  </p>
                </div>
              </div>

              <div className="flex gap-3 p-4 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-800">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center shadow-md">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900 dark:text-white mb-0.5">RC Auto e prodotti assicurativi</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Una piattaforma dedicata permetterà agli Studi aderenti di accedere a oltre 30 compagnie e circa 90 prodotti assicurativi, con procedure di emissione rapide e digitalizzate.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer Modale */}
            <div className="p-4 sm:p-6 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
              <PraticheAutoOnlineAccess />
            </div>
          </div>
        </div>
      )}

      {/* Modale Video Tutorial */}
      {showVideoModal && (
        <VideoModal
          title="Video Tutorial"
          subtitle="Scopri come utilizzare l'Area Riservata"
          embedSrc="https://www.youtube.com/embed/rw4UbW02nlU?si=tHXhUKfBGyQX_jyf"
          accent="purple"
          onClose={() => setShowVideoModal(false)}
          footer={
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
              💡 <strong>Suggerimento:</strong> Guarda il video per scoprire tutte le funzionalità dell'Area Riservata
            </p>
          }
        />
      )}

      {/* Popup video benvenuto Rete Certificata */}
      {showRCWelcomeVideo && certifiedNetworkStatus?.welcomeVideo && (
        <VideoModal
          title={certifiedNetworkStatus.welcomeVideo.title}
          subtitle="Rete Certificata UNASCA"
          embedSrc={`https://www.youtube-nocookie.com/embed/${extractYouTubeId(certifiedNetworkStatus.welcomeVideo.url)}`}
          accent="blue"
          onClose={closeRCWelcomeVideo}
          footer={
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rcVideoDontShowAgain}
                  onChange={(e) => setRcVideoDontShowAgain(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Non mostrare più questo video
                </span>
              </label>
              <button
                onClick={closeRCWelcomeVideo}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Chiudi
              </button>
            </div>
          }
        />
      )}
    </div>
    </>
  );
};

export default AssociateDashboardPage;

